// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import getOrderStatusMappingStatusColor from "@store/Framework/Common/Utilities/getOrderStatusMappingStatusColor";
import { orderStatusColoredStyles } from "@store/Themes/common/mixins/order";
import { tableGetEllipsisCellProps, tableProps } from "@store/Themes/common/props/table";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
/* Starter Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    orderStatusMappingDataView: getOrderStatusMappingDataView(state), // Starter Custom Code
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // Starter Custom Code
    orderNumberCell?: DataTableCellProps;
    dateCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    customerPOCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    totalCell?: DataTableCellProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    shipToCell: tableGetEllipsisCellProps(290),
    customerPOCell: tableGetEllipsisCellProps(95),
    statusCell: tableGetEllipsisCellProps(95),
    totalCell: tableProps.amountCell,
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    extendedStyles,
    // Starter Custom Code
    orderStatusMappingDataView,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    /* Starter Custom Code - Start */
    const statusColor = React.useMemo(
        () => getOrderStatusMappingStatusColor(order, orderStatusMappingDataView.value),
        [order, orderStatusMappingDataView.value],
    );

    const statusCellCss = React.useMemo(() => {
        if (!statusColor) {
            return styles.statusCell;
        }

        return mergeProps<OrderSummaryCardStyles["statusCell"]>(styles.statusCell, {
            css: orderStatusColoredStyles(statusColor),
        });
    }, [styles.statusCell, statusColor]);

    const orderNumber = orderSettings.showWebOrderNumber
        ? order.webOrderNumber || order.erpOrderNumber
        : order.erpOrderNumber || order.webOrderNumber;

    const shipTo = `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`;

    return (
        <DataTableRow key={order.id}>
            <DataTableCell {...styles.orderNumberCell} data-test-selector={`orderSummaryCard_${orderNumber}`}>
                {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
            </DataTableCell>
            <DataTableCell {...styles.dateCell} data-test-selector="orderSummaryCard_Date">
                {orderDateDisplay}
            </DataTableCell>
            <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>
            <DataTableCell {...styles.customerPOCell}>{order.customerPO}</DataTableCell>
            <DataTableCell {...statusCellCss}>{order.statusDisplay}</DataTableCell>
            <DataTableCell {...styles.totalCell}>{order.orderGrandTotalDisplay}</DataTableCell>
        </DataTableRow>
    );
    /* Starter Custom Code - End */
};

export default connect(mapStateToProps)(OrderSummaryCard);
